/**
 * What an account HOLDS, read from its entitlements (APP-ENTITLEMENT-001).
 *
 * An entitlement whose status is one of `HELD_ENTITLEMENT_STATUSES` (trialing, active, read-only) holds its app and
 * therefore every capability key that app grants; `off` holds nothing. Read-only still HOLDS: the app opens, its
 * writes are refused by the gate (`APP_READ_ONLY`), nothing is hidden.
 *
 * This package requires nothing, so it answers only what the CATALOG says an app grants. The server-side resolver
 * (`@puffin-core/app-entitlements`) hands the result to `@puffin-core/tenant-capabilities`' `heldCapabilitiesOf`,
 * which adds every always-on capability whose host is held — ONE rule for that, kept where it lives today.
 *
 * @module @puffin-core/app-catalog/rules/heldCapabilities
 */

'use strict';

const { getApp } = require('../catalog');
const { HELD_ENTITLEMENT_STATUSES } = require('../vocabulary');

/**
 * @typedef {Object} EntitlementLike
 * @property {string} appKey
 * @property {string} status - one of `ENTITLEMENT_STATUS_VALUES`.
 */

/**
 * @param {EntitlementLike} entitlement
 * @returns {boolean} true when the account holds the app (trialing, active or read-only).
 */
function isHeld(entitlement) {
  return HELD_ENTITLEMENT_STATUSES.includes(entitlement.status);
}

/**
 * The app keys an account holds, in catalog order of the input (duplicates collapse).
 *
 * @param {Iterable<EntitlementLike>} entitlements
 * @returns {Set<string>} a fresh set; an unknown app key is DROPPED (a row the catalog no longer names grants
 *   nothing — fail closed).
 */
function heldAppKeys(entitlements) {
  const held = new Set();
  for (const entitlement of entitlements) {
    if (isHeld(entitlement) && getApp(entitlement.appKey) !== null) held.add(entitlement.appKey);
  }
  return held;
}

/**
 * The capability keys an account holds through its entitlements: the union of every held app's `capabilityKeys`.
 *
 * @param {Iterable<EntitlementLike>} entitlements
 * @returns {Set<string>} a fresh set.
 */
function capabilitiesHeldBy(entitlements) {
  const capabilities = new Set();
  for (const appKey of heldAppKeys(entitlements)) {
    for (const capabilityKey of getApp(appKey).capabilityKeys) capabilities.add(capabilityKey);
  }
  return capabilities;
}

module.exports = { isHeld, heldAppKeys, capabilitiesHeldBy };
